/**
 * Utility functions for masking sensitive information like SSN, ITIN, and identification numbers
 * 
 * This module provides standardized masking for sensitive personal information
 * across the application. By centralizing this logic, we ensure consistent 
 * formatting and make it easier to modify masking behavior if requirements change.
 * 
 * Used in:
 * - Identification.vue
 * - CoApplicant.vue
 * - And any other components that need to mask sensitive information
 */
import { IdentificationType } from '@/constants';

/**
 * Creates a masked version of an identification number based on ID type
 * @param {Number} identificationType - The type of identification (from IdentificationType enum)
 * @returns {String} A string of asterisks appropriate for the ID type
 */
export function getMaskedIdentificationNumber(identificationType) {
  if (identificationType === IdentificationType.DRIVERSLICENSE) {
    return '**********'; // 10 asterisks for driver's license
  } else if (identificationType === IdentificationType.PASSPORT) {
    return '*********'; // 9 asterisks for passport
  }
  return '********'; // 8 asterisks default for other ID types
}

/**
 * Returns a masked SSN/ITIN in the standard format
 * @returns {String} Masked SSN/ITIN with dashes
 */
export function getMaskedTIN() {
  return '***-**-****';
}

/**
 * Creates a masked version of a credit card number
 * Shows only the last 4 digits
 * @param {String} cardNumber - The full credit card number
 * @returns {String} Masked credit card number
 */
export function getMaskedCreditCard(cardNumber) {
  if (!cardNumber) return '****************';
  
  const lastFour = cardNumber.slice(-4);
  return `**** **** **** ${lastFour}`;
}

/**
 * Creates a masked version of an email address
 * Shows the first character, followed by asterisks, @ symbol, and the domain
 * @param {String} email - The email address to mask
 * @returns {String} Masked email address
 */
export function getMaskedEmail(email) {
  if (!email || !email.includes('@')) return email;
  
  const [localPart, domain] = email.split('@');
  if (localPart.length <= 1) return email;
  
  const firstChar = localPart.charAt(0);
  const maskedLocalPart = firstChar + '*'.repeat(localPart.length - 1);
  
  return `${maskedLocalPart}@${domain}`;
}
